/**
 * Services Management Page
 * View, add, edit, and manage services
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  RotateCcw,
  Upload,
  X,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { adminAPI } from '../api/adminEndpoints';
import { formatDate } from '../utils/helpers';
import { ITEMS_PER_PAGE } from '../utils/constants';
import type { Service, ServiceFormData, Category } from '../api/adminTypes';
import Card from '../components/Card';
import { useToastNotifications } from '../hooks/useToast';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import { uploadImage, validateImageFile } from '../utils/imageUpload';
import './ContentManagement.css';

// This URL is assumed to end with a trailing slash. Added fallback to prevent undefined issues.
const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

export default function Services() {
  const { success, error, warning, clearToasts } = useToastNotifications();
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Sorting functionality
  const [sortField, setSortField] = useState<'name' | 'status' | 'createdAt' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for Image Preview Feature (NEW)
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    categoryId: '',
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // uploadedImage state holds the S3 Key (e.g., 'temp/file.jpg') OR the full URL if uploadImage returns it
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [originalService, setOriginalService] = useState<Service | null>(null);

  // Options for dropdowns
  const statusOptions = [
    { label: 'All Status', value: '' },
    { label: 'Active', value: 'active', color: 'text-green-600' },
    { label: 'Inactive', value: 'inactive', color: 'text-red-600' },
  ];

  const categoryOptions = [
    { label: 'All Categories', value: '' },
    ...categories.map((cat) => ({ label: cat.name, value: cat.id })),
  ];

  const fetchServices = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const shortBy = sortField || undefined;
      const shortType = sortOrder || undefined;

      const data = await adminAPI.services.getServices(
        ITEMS_PER_PAGE,
        offset,
        search,
        selectedCategory,
        selectedStatus,
        shortBy,
        shortType
      );

      // No client-side sorting needed
      setServices(data.result);
      setTotal(data.counts);

      // Show toast if no records found and filters are applied
      if (data.result.length === 0 && (search || selectedCategory || selectedStatus)) {
        warning('No records found matching your filters', 'Try adjusting your search criteria');
      }
    } catch (error: any) {
      console.error('Failed to fetch services:', error);
      if (error.message?.includes('404') || error.status === 404) {
        setServices([]);
        setTotal(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, search, selectedCategory, selectedStatus, sortField, sortOrder]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await adminAPI.categories.getCategories(100, 0, '');
      // Handle different response structures
      let categoriesList: Category[] = [];
      if (data.data) {
        categoriesList = data.data;
      } else if (data.result) {
        categoriesList = data.result;
      }

      const activeCategories = categoriesList.filter((cat) => cat.status === 'active');
      setCategories(activeCategories);

      if (activeCategories.length === 0) {
        console.warn('No active categories found');
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, [fetchServices, fetchCategories]);

  // Update searchInput immediately as user types
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  // Debounce the actual search to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleCategoryFilter = (value: string) => {
    setSelectedCategory(value);
    setPage(1);
  };

  const handleStatusFilter = (value: string) => {
    setSelectedStatus(value);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearch('');
    setSearchInput('');
    setSelectedCategory('');
    setSelectedStatus('');
    setSortField(null);
    setSortOrder(null);
    setPage(1);
    success('All filters reset', 'Filters have been cleared');
  };

  // NEW: Handler for image preview with TS fix
  const handleImagePreview = (imageKey: string | null | undefined) => {
    if (imageKey) {
      // This logic assumes service.image stores the S3 Key (e.g., 'temp/file.jpg')
      setPreviewImageUrl(`${IMAGE_URL}${imageKey}`);
      setIsPreviewModalOpen(true);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Service name is required';
    }
    if (!formData.categoryId) {
      errors.categoryId = 'Category is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddClick = () => {
    setFormData({
      name: '',
      categoryId: '',
      image: null,
    });
    setFormErrors({});
    setUploadedImage(null);
    setIsAddModalOpen(true);
  };

  const handleEditClick = (service: Service) => {
    // 💥 NEW: Store the original service data
    setOriginalService(service);

    setFormData({
      name: service.name,
      categoryId: service.categoryId,
      image: service.image || null, // S3 Key goes into formData
      imageRemove: false,
    });
    setEditingId(service.id);
    setFormErrors({});

    // Set uploadedImage for visual preview. Check if image is an S3 key or a full URL.
    const imageToPreview = service.image
      ? service.image.startsWith('http')
        ? service.image
        : `${IMAGE_URL}${service.image}`
      : null;

    setUploadedImage(imageToPreview); // Full URL or S3 key prepended with IMAGE_URL for display

    setIsEditModalOpen(true);
  };

  const handleAddSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // formData.image contains the S3 key set by handleImageUpload
      await adminAPI.services.createService(formData);
      await fetchServices();
      setIsAddModalOpen(false);
      setFormData({
        name: '',
        categoryId: '',
        image: null,
      });
      setUploadedImage(null);
      success('Service created successfully!', 'New service has been added');
    } catch (err: any) {
      console.error('Failed to create service:', err);
      error('Failed to create service', 'Please try again later');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !editingId || !originalService) return;

    // 💥 UPDATED LOGIC: Create a patch object with only changed fields
    const patch: Partial<ServiceFormData> = {};

    if (formData.name !== originalService.name) {
      patch.name = formData.name;
    }

    if (formData.categoryId !== originalService.categoryId) {
      patch.categoryId = formData.categoryId;
    }

    // Check for image removal or changes
    if (formData.imageRemove) {
      patch.imageRemove = true;
    } else if (formData.image !== (originalService.image || null)) {
      patch.image = formData.image;
    }

    // If no changes were detected, close modal and exit
    if (Object.keys(patch).length === 0) {
      warning('No changes detected', 'Make some changes before saving');
      setIsEditModalOpen(false);
      return;
    }

    // End of patch creation

    setIsSubmitting(true);
    try {
      // Show immediate success feedback
      success('Service updated successfully!', 'Service has been updated');

      // Send ONLY the patch object (which contains only changed fields)
      await adminAPI.services.updateService(editingId, patch);

      await fetchServices();
      setIsEditModalOpen(false);
      setEditingId(null);
      setUploadedImage(null);
      setOriginalService(null); // Cleanup
    } catch (err: any) {
      console.error('Failed to update service:', err);
      error('Failed to update service', 'Please try again later');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = (service: Service) => {
    const newStatus = service.status === 'active' ? 'inactive' : 'active';

    if (newStatus === 'inactive') {
      // Clear previous popups/toasts immediately so only latest status popup is visible
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      setIsDeleteModalOpen(false);
      setIsPreviewModalOpen(false);
      setDeletingId(null);
      setEditingId(null);
      setUploadedImage(null);
      setOriginalService(null);
      clearToasts();
    }

    // 1. Show immediate success feedback
    success(`Service status updated to ${newStatus}`, 'Status changed successfully');

    // 2. Update UI instantly
    setServices((prev) => prev.map((s) => (s.id === service.id ? { ...s, status: newStatus } : s)));

    // 3. Background API call (silent, no additional toast)
    adminAPI.services.updateService(service.id, { status: newStatus }).catch((err) => {
      console.error('Failed to update service:', err);
      error('Failed to update service status', 'The change has been reverted');

      // 4. Revert UI on error
      setServices((prev) =>
        prev.map((s) =>
          s.id === service.id ? { ...s, status: service.status as 'active' | 'inactive' } : s
        )
      );
    });
  };

  const handleDeleteClick = (serviceId: string) => {
    setDeletingId(serviceId);
    setIsDeleteModalOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      error(validationError, 'Please select a valid image file');
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadImage(file);
      setUploadedImage(result.url);

      // We set the form data's image property to the S3 key (result.fileName)
      // for storage in the database.
      setFormData({
        ...formData,
        image: result.fileName,
      });
      success('Image uploaded successfully', 'Image has been uploaded');
    } catch (err: any) {
      console.error('Image upload failed:', err);
      error('Failed to upload image', 'Please try again later');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    // uploadedImage state controls the visual preview (set to null)
    setUploadedImage(null);
    // formData.image state controls the S3 key sent to the backend (set to null)
    // and set imageRemove flag to true
    setFormData({ ...formData, image: null, imageRemove: true });
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;

    setIsDeleting(true);

    try {
      await adminAPI.services.deleteService(deletingId);
      if (services.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        await fetchServices();
      }
      setIsDeleteModalOpen(false);
      setDeletingId(null);
      success('Service deleted successfully', 'Service has been removed');
    } catch (error) {
      console.error('Failed to delete service:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        <div className="grid grid-cols-8 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
          <div className="dark:text-white col-span-1" style={{ paddingLeft: '10px' }}>
            Image
          </div>
          <div className="dark:text-white col-span-2" style={{ paddingLeft: '6px' }}>
            Service Name
          </div>
          <div className="text-gray-700 dark:text-white col-span-2">Category</div>
          <div className="text-gray-700 dark:text-white col-span-1" style={{ paddingLeft: '15px' }}>
            Created At
          </div>
          <div className="text-gray-700 dark:text-white col-span-1" style={{ paddingLeft: '10px' }}>
            Status
          </div>
          <div className="text-gray-700 dark:text-white text-center col-span-1">Actions</div>
        </div>

        {/* Loader in table area */}
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="overflow-hidden">
      {/* Search and Filters */}
      <Card className="mb-2 p-[10px]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          {/* Title + Add Button */}
          <div className="flex items-center gap-7 mb-4 sm:mb-0">
            <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">
              Services Management
            </h1>
            <button
              onClick={handleAddClick}
              className="w-8 h-8 flex items-center justify-center bg-primary-500 text-white rounded-full 
             hover:bg-primary-600 transition-colors text-2xl font-extrabold leading-none"
              title="Add Service"
            >
              +
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap lg:items-center lg:justify-end gap-3 w-full">
            {/* Total Services */}
            <div className="flex items-center">
              <div
                className="flex items-center gap-3 bg-gray-100 px-6 h-10
          rounded-xl text-gray-800 text-base font-medium shadow-sm w-full sm:w-auto justify-center"
              >
                <span>Total Services: {total}</span>
              </div>
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64 h-10">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full h-full pl-10 pr-4 border border-gray-300 rounded-lg 
            focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Category Filter */}
            <div className="relative h-10 flex-shrink-0 transition-all duration-200">
              <Listbox value={selectedCategory || 'all'} onChange={handleCategoryFilter}>
                {({ open }) => {
                  return (
                    <>
                      <Listbox.Button
                        className={`inline-flex items-center h-full pl-4 pr-10 text-left bg-white border border-gray-300
              rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500
              transition-all duration-200 hover:border-gray-400 cursor-pointer
              min-w-[140px] max-w-[400px]`}
                      >
                        <span className="block">
                          {categoryOptions.find((o) => o.value === selectedCategory)?.label ||
                            'Category'}
                        </span>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </Listbox.Button>

                      <Listbox.Options className="absolute z-20 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[140px] max-w-[400px]">
                        {categoryOptions.map((option) => (
                          <Listbox.Option
                            key={option.value}
                            value={option.value}
                            className={({ active }) =>
                              `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100 text-primary-900' : 'text-gray-900'}`
                            }
                          >
                            <span className="whitespace-nowrap">{option.label}</span>
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </>
                  );
                }}
              </Listbox>
            </div>

            {/* Status Filter */}
            <div
              className={`relative flex-shrink-0 transition-all duration-200
          ${!selectedStatus || selectedStatus === 'all' ? 'w-[140px]' : 'w-[162px]'}`}
            >
              <Listbox value={selectedStatus} onChange={handleStatusFilter}>
                <Listbox.Button
                  className="w-full h-10 pl-4 pr-10 text-left bg-white border border-gray-300 
              rounded-lg shadow-sm focus:outline-none focus:ring-2 
              focus:ring-primary-500 transition-all duration-200 hover:border-gray-400 
              cursor-pointer flex items-center overflow-hidden"
                >
                  <span className="block truncate">
                    {statusOptions.find((o) => o.value === selectedStatus)?.label || 'Status'}
                  </span>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </Listbox.Button>

                <Listbox.Options className="absolute z-20 mt-2 w-full bg-white rounded-md shadow-lg border border-gray-200 text-sm">
                  {statusOptions.map((option) => (
                    <Listbox.Option
                      key={option.value}
                      value={option.value}
                      className={({ active }) =>
                        `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                      }
                    >
                      <span className={option.color}>{option.label}</span>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Listbox>
            </div>

            {/* Reset Filters */}
            <Button
              variant="secondary"
              onClick={handleResetFilters}
              className="px-4 h-10"
              style={{ width: '162px' }}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Services Table */}
      <Card>
        {isLoading ? (
          <TableShimmer />
        ) : services.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-8 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div style={{ paddingLeft: '10px' }} className="dark:text-white col-span-1">
                  Image
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-2"
                  style={{ paddingLeft: '6px' }}
                  onClick={() => {
                    if (sortField === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('name');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Service Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div className="dark:text-white col-span-2">Category</div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-1"
                  style={{ paddingLeft: '15px' }}
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  Created At {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-1"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => {
                    if (sortField === 'status') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('status');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div className="text-center dark:text-white col-span-1">Actions</div>
              </div>
              {/* Empty State inside table */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className=" mx-auto mb-4 " />
                <Button onClick={handleAddClick} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Service
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <div className="table-min-w">
                {/* Grid Header */}
                <div className="grid grid-cols-8 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                  <div style={{ paddingLeft: '10px' }} className="dark:text-white col-span-1">
                    Image
                  </div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-2"
                    style={{ paddingLeft: '6px' }}
                    onClick={() => {
                      if (sortField === 'name') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('name');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Service Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="dark:text-white col-span-2">Category</div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-1"
                    style={{ paddingLeft: '15px' }}
                    onClick={() => {
                      if (sortField === 'createdAt') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('createdAt');
                        setSortOrder('desc');
                      }
                      setPage(1);
                    }}
                  >
                    Created At {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-1"
                    style={{ paddingLeft: '10px' }}
                    onClick={() => {
                      if (sortField === 'status') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('status');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="text-center dark:text-white col-span-1">Actions</div>
                </div>
                {/* Grid Body */}
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      className="grid grid-cols-8 gap-3 p-4 hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors items-center cursor-pointer p-[8px]"
                    >
                      <div className="col-span-1">
                        <div
                          className="flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                          onClick={() => service.image && handleImagePreview(service.image)}
                        >
                          <img
                            src={service.image ? `${IMAGE_URL}${service.image}` : '/service.png'}
                            alt={service.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = '/service.png';
                            }}
                          />
                        </div>
                      </div>
                      <div className="font-medium dark:text-white min-w-0 flex-1 col-span-2">
                        {service.name}
                      </div>
                      <div className="text-sm text-gray-600 truncate dark:text-gray-300 col-span-2">
                        {service.categoryInfo.name}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 col-span-1">
                        {formatDate(service.createdAt)}
                      </div>
                      <div className="col-span-1">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${
                            service.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {service.status}
                        </span>
                      </div>
                      <div className="col-span-1">
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => handleToggleStatus(service)}
                            className={`p-2 rounded-lg transition-colors ${
                              service.status === 'active'
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            title={`Mark as ${service.status === 'active' ? 'inactive' : 'active'}`}
                          >
                            {service.status === 'active' ? (
                              <ToggleRight className="w-7 h-7" />
                            ) : (
                              <ToggleLeft className="w-7 h-7" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEditClick(service)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-6 h-6" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(service.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-6 h-6" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(page * ITEMS_PER_PAGE, total)} of {total} services
              </div>
              <div className="flex gap-2">
                <div className="flex items-center justify-between gap-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-4 py-2 text-sm font-medium text-gray-700">
                    Page {page} of {Math.ceil(total / ITEMS_PER_PAGE) || 1}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(total / ITEMS_PER_PAGE) || total === 0}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Add Service Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Service"
      >
        <div className="space-y-4">
          <Input
            label="Service Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            placeholder="Enter service name"
          />

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Image</label>
            <div className="mt-1 flex items-center space-x-4">
              {uploadedImage ? (
                <div className="relative">
                  <img
                    // Here uploadedImage holds the full preview URL from uploadImage utility
                    src={uploadedImage || undefined}
                    alt="Service preview"
                    className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="service-image-upload"
                />
                <label
                  htmlFor="service-image-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {isUploading ? 'Uploading...' : uploadedImage ? 'Change Image' : 'Upload Image'}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP up to 5MB</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.categoryId}
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              className={`w-full h-11 px-4 border ${formErrors.categoryId ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 shadow-sm transition-all duration-200 hover:border-gray-400`}
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {formErrors.categoryId && (
              <p className="text-xs text-red-500 mt-1">{formErrors.categoryId}</p>
            )}
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <Button variant="secondary" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSubmit} loading={isSubmitting}>
              Create Service
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Service Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Service"
      >
        <div className="space-y-4">
          <Input
            label="Service Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            placeholder="Enter service name"
          />

          {/* Image Upload for Edit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Image</label>
            <div className="mt-1 flex items-center space-x-4">
              {uploadedImage ? (
                <div className="relative">
                  <img
                    src={uploadedImage || undefined}
                    alt="Service preview"
                    className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="service-image-upload-edit"
                />
                <label
                  htmlFor="service-image-upload-edit"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {isUploading ? 'Uploading...' : uploadedImage ? 'Change Image' : 'Upload Image'}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP up to 5MB</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.categoryId}
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              className={`w-full h-11 px-4 border ${formErrors.categoryId ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 shadow-sm transition-all duration-200 hover:border-gray-400`}
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {formErrors.categoryId && (
              <p className="text-xs text-red-500 mt-1">{formErrors.categoryId}</p>
            )}
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} loading={isSubmitting}>
              Update Service
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Service"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this service? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} loading={isDeleting}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* --- Image Preview Modal (NEW) --- */}
      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title="Image Preview"
        size="lg" // Use a larger size for the image
      >
        <div className="flex justify-center items-center p-4">
          {previewImageUrl ? (
            <img
              src={previewImageUrl || undefined}
              alt="Full Service Preview"
              // Ensure the image fits within the modal window
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-xl"
            />
          ) : (
            <p className="text-gray-500">No image available.</p>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="secondary" onClick={() => setIsPreviewModalOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
}
